import FilterListIcon from "@mui/icons-material/FilterList"
import { Menu, useMediaQuery, useTheme } from "@mui/material"
import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import { IconButton } from "src/components/common/iconButton"
import { InputField } from "src/components/common/inputfield"

import { SideNav, SideNavItemProps } from "src/components/common/sideNav"
import { FilterSortSelection } from "src/components/core/filterSortSelector/FilterSortSelector"
import { create } from "src/helpers/bem"
import { Category, useCategories } from "src/states/shop/category"
import { FilterSorting, mergeFilter, useFilter } from "src/states/shop/filter"
import { mergeProducts, useProducts } from "src/states/shop/products"
import { TranslationMessages } from "src/translations"

import styles from "./ShopOverviewProductsHeader.module.scss"

const bem = create(styles, "ShopOverviewProductsHeader")

export type ShopOverviewProductsHeaderProps = {
  messages: TranslationMessages
}

export const productsSortingOptions = [
  "default",
  "favorite",
  "alphabeticUp",
  "alphabeticDown",
  "priceUp",
  "priceDown",
] as FilterSorting[]

export const ShopOverviewProductsHeader: FC<
  ShopOverviewProductsHeaderProps
> = ({ messages }) => {
  const m = messages.pages.shop.overview.header
  const { products, loading: loadingProducts } = useProducts()
  const { filterBy, search, sortBy } = useFilter()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("xl"))
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const openFilter = Boolean(anchorEl)
  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleFilterClose = () => {
    setAnchorEl(null)
  }
  const { loading, categories } = useCategories()
  const onFilterClick = (categoryId: string) => {
    const tmpcategories = [
      ...categories,
      ...categories.flatMap((c) => c.children?.map((sc) => sc)),
    ]
    console.log(tmpcategories)
    const category = tmpcategories.find((c) => c?.id === categoryId) as Category
    mergeFilter({ filterBy: { category } })
    handleFilterClose()
  }
  const formattedCategories = categories.map((category) => ({
    ...category,
    active:
      filterBy?.category?.id === category.id ||
      filterBy?.category?.parentCategoryId == category.id ||
      (category.id === "default" && !filterBy),
    onClick: () => onFilterClick(category.id),
    title: category.name,
    child: category.children?.map((subCategory) => ({
      ...subCategory,
      active: filterBy?.category?.id === subCategory.id,
      title: subCategory.name,
      onClick: () => onFilterClick(subCategory.id!),
    })),
  }))

  const isAllProductsCategory = (category: Category) =>
    category.categoryType === "category" && category.children === undefined

  const onFilter = useCallback(async () => {
    if (products.length === 0 || loadingProducts) return

    let result = [...products]

    if (
      filterBy?.category?.id &&
      !isAllProductsCategory(filterBy.category as Category)
    ) {
      result = result.filter((p) =>
        [p.categoryId, p.parentCategoryId].includes(filterBy.category?.id),
      )
    }

    result = result.filter(
      (p) =>
        p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.note?.toLowerCase().includes(search.toLowerCase()),
    )

    switch (sortBy) {
      case "alphabeticUp":
        result.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
        break
      case "alphabeticDown":
        result.sort((a, b) => (b.name ?? "").localeCompare(a.name ?? ""))
        break
      case "priceUp":
        result.sort((a, b) => (a.price ?? 0) - (b.price ?? 0))
        break
      case "priceDown":
        result.sort((a, b) => (b.price ?? 0) - (a.price ?? 0))
        break
      case "favorite":
        result.sort((a, _) => (a.favorite ? -1 : 1))
        break
    }

    mergeProducts({
      filteredProducts: result.filter((p) => p.isValid),
    })
  }, [filterBy, products, search, sortBy, loadingProducts])

  // onFilter is intentionally excluded from deps: products changes are
  // handled by loadProducts in ShopOverview, the filter effect should only
  // run when the user changes filter criteria (filterBy, sortBy, search).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onFilter()
  }, [filterBy, sortBy, search])

  const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    mergeFilter({
      search: e?.target?.value ?? "",
    })
  }

  const handleChangeSorting = (value: string) => {
    const sortingValue = value as FilterSorting
    mergeFilter({
      sortBy: sortingValue,
    })
  }

  const sortingItems = useMemo(
    () =>
      productsSortingOptions.map((key) => ({
        id: key,
        label: m.sorting.items?.[key],
      })),
    [m.sorting.items],
  )

  const renderFilter = () => (
    <>
      <IconButton
        aria-controls={openFilter ? "filter-menu" : undefined}
        aria-expanded={openFilter}
        aria-haspopup="true"
        className={bem("filter__button")}
        icon={FilterListIcon}
        id="filter-button"
        onClick={handleFilterClick}
      />
      <Menu
        disablePortal
        anchorEl={anchorEl}
        className={bem("filter__menu")}
        id="filter-menu"
        open={openFilter}
        MenuListProps={{
          "aria-labelledby": "filter-button",
        }}
        onClose={handleFilterClose}
      >
        <SideNav
          items={formattedCategories as ReadonlyArray<SideNavItemProps>}
        />
      </Menu>
    </>
  )
  return (
    <div className={bem()}>
      <div className={bem("toolbar")}>
        <div className={bem("search")}>
          <InputField
            label={m.search.input}
            name="search"
            sizeVariant="responsive"
            type="text"
            value={search}
            classes={{
              root: bem("input"),
            }}
            onChange={handleChangeSearch}
          />
          {isMobile && renderFilter()}
        </div>

        <FilterSortSelection
          className={bem("filter")}
          options={sortingItems}
          value={sortBy}
          onChange={handleChangeSorting}
        />
      </div>
    </div>
  )
}
